// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ShipmentValueDisplay } from '@/shipment/components/shipment-value-display';
import { formatCurrency } from '@/utilities/number';
import { Tooltip, Typography } from '@mui/material';
import { Decimal } from 'decimal.js';

interface Props {
  /** The shipment's locked total, present once it ships or is received. */
  actualValue: string | null;
  /** The estimate captured before the shipment locked. */
  estimatedValue: string | null;
  /** What the shipment's held units are estimated to be worth. */
  unconfirmedValue: Decimal | null;
}

/**
 * A shipment's total value.
 *
 * The shipment's own total covers only the units that made it through, since
 * held units have no asset and are excluded from the line quantities behind the
 * estimate. When a shipment is holding units the total therefore understates what
 * arrived, so the held estimate is added back and the split shown on hover.
 * Nothing is held on most shipments, so the plain total is the common case.
 */
export function ShipmentTotalValue(props: Props) {
  const confirmed = props.actualValue ?? props.estimatedValue;

  if (props.unconfirmedValue === null || props.unconfirmedValue.isZero()) {
    return (
      <ShipmentValueDisplay
        actualValue={props.actualValue}
        estimatedValue={props.estimatedValue}
        variant="subtitle2"
      />
    );
  }

  const combined = new Decimal(confirmed ?? 0).plus(props.unconfirmedValue);
  // Nothing has locked the shipment's own total yet, so the whole figure is a
  // projection and has to keep reading as one.
  const isEstimate = props.actualValue === null;

  const tooltip = (
    <>
      {confirmed !== null && (
        <Typography variant="caption" component="div">
          {isEstimate ? 'Estimated' : 'Confirmed'}: {formatCurrency(confirmed)}
        </Typography>
      )}
      <Typography variant="caption" component="div">
        Unconfirmed: {formatCurrency(props.unconfirmedValue.toFixed(2))}
      </Typography>
    </>
  );

  return (
    <Tooltip arrow title={tooltip}>
      <Typography
        variant="subtitle2"
        sx={{
          color: isEstimate ? 'text.secondary' : undefined,
          fontStyle: isEstimate ? 'italic' : undefined,
          textDecoration: 'underline dotted',
          cursor: 'help',
        }}
      >
        {formatCurrency(combined.toFixed(2))}
      </Typography>
    </Tooltip>
  );
}
